<template>
  <div>
    <div class="d-flex flex-column flex-lg-row">
      <div class="pr-lg-4">
        <strong class="mb-3 d-block">{{ t('Calendar') }}</strong>
        <VueDatePicker
          inline
          range
          enable-time-picker
          :locale="locale"
          :disabled-dates="disabledDates"
          text-input
          input-class-name="form-control"
          :model-value="range"
          @update:model-value="range = $event"
          utc
          :markers="markers"
          :loading="loading"
          :enable-time-picker="false"
          @update-month-year="handleMonthYear"
          auto-apply>
        </VueDatePicker>
      </div>
      <!-- Nothing to show before an equipment is chosen: the flow can start with the dates. -->
      <div class="flex-grow-1" v-if="props.booking?.equipment">
        <strong class="mb-3 d-block">{{ t('Events') }}</strong>
        <!-- duplicated from resources/js/components/equipment/EquipmentCalendarList.vue -->
        <ul class="list-group overflow-auto" style="max-height: 320px">
          <li v-for="event in histories"
              :class="[
                'list-group-item list-group-item-action d-flex align-items-start'
              ]"
            >
            <span :class='[
              event.type === "Tour" ? "text-secondary" : "",
              event.type === "Allotment" && ! event.resource.allow_booking ? "text-danger" : event.type === "Allotment" ? "text-warning" : "",
              event.type === "ProductTest" ? "text-primary" : "",
              event.type === "Booking" ? "text-info" : "",
              ]'><i class="fa fa-circle"></i></span>
            <div class="ml-2">
              <strong v-if="event.type === 'Tour'">
                {{ event.resource.name }}
              </strong>
              <strong v-if="event.type === 'Allotment'">
                {{ event.resource.title }}
              </strong>
              <strong v-if="event.type === 'ProductTest'">
                Test {{ event.resource.tour.name }}
              </strong>
              <strong v-if="event.type === 'Booking'">
                Booking {{ event.resource.tour.name }}
              </strong>
              <small class="text-muted d-block">
                {{ t('Start at:') }} {{ dayjs(event.start_at).format('lll') }}
                <br>
                {{ t('End at:') }} {{ dayjs(event.end_at).format('lll') }}
              </small>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import HistoryModel from '../../models/History'

const { t, locale } = useI18n()

const emit = defineEmits(['change'])

const props = defineProps({
  timezone: {
    type: String,
    required: true
  },
  booking: {
    type: Object,
    required: false
  },
  defaultStartTime: {
    type: String,
    default: '08:00'
  },
  defaultEndTime: {
    type: String,
    default: '18:00'
  },
  /**
   * Show the availability without offering to pick a period. Month navigation still works, so the
   * calendar stays browsable; only the selection is off.
   */
  readonly: {
    type: Boolean,
    default: false
  },
})

// A `default` does not cover an explicitly passed null, and the tour only exposes these times in
// date mode, so fall back here rather than dereferencing null.
const atTime = (time, fallback) => {
  const [hour, minute] = (time || fallback).split(':')
  return dayjs().set('hour', hour).set('minute', minute)
}

// Read-only means there is nothing to pick, so nothing is highlighted either: a default range
// shown as selected would read as a choice the user cannot undo.
const range = ref(props.readonly ? null : [
  props.booking?.start_at ?? atTime(props.defaultStartTime, '08:00'),
  props.booking?.end_at ?? atTime(props.defaultEndTime, '18:00'),
])

const attributes = ref([])
const histories = ref([])
const markers = ref([])
const loading = ref(false)

/**
 * Read-only is expressed by disabling every day rather than with the picker's own `readonly`:
 * that one lays a full-surface transparent overlay (.dp__menu_readonly, inset 0, z-index 999999)
 * over the menu, which also swallows the month navigation. Browsing months is the whole point of
 * looking at availability, so only the cells are switched off here.
 */
const disabledDates = computed(() => (props.readonly ? () => true : []))

// Immediate: the picker opens on a default range, so that range is what the user sees selected
// and must be what gets staged, even if they save without touching it. A read-only calendar
// reports nothing at all: it is there to be looked at, not to choose a period.
watch(range, (newRange) => {
  if (props.readonly) {
    return;
  }

  emit('change', {
    start: dayjs(newRange[0]),
    end: dayjs(newRange[1]),
  });
}, { deep: true, immediate: true })

// Remembered so the markers can be refreshed for the month on screen.
let currentMonth = dayjs().month()
let currentYear = dayjs().year()

const handleMonthYear = async ({ month, year }) => {
  getHistory(month, year)
}

// The equipment can be chosen after the dates, in which case its markers arrive later.
watch(() => props.booking?.equipment?.id, () => {
  getHistory(currentMonth, currentYear)
})

const getHistory = async (month, year) => {
  currentMonth = month
  currentYear = year

  if (props.booking?.equipment?.id === undefined) {
    histories.value = []
    markers.value = []
    return;
  }
  const historyModel = new HistoryModel()

  loading.value = true

  let historyResponse
  try {
    historyResponse = await historyModel.where('equipment_id', props.booking.equipment.id)
      .where('period', [
        (new Date(Date.UTC(year, month, 1))).toISOString().split('T')[0],
        (new Date(Date.UTC(year, month + 1, 0))).toISOString().split('T')[0],
      ].join(':'))
      // Without this the endpoint paginates at 20 and a busy month loses markers.
      .limit(-1)
      .get();
  } finally {
    loading.value = false
  }

  const contentColor = {
    booking: {
      draft: 'gray',
      pending: 'gray',
      validated: 'green',
      refused: 'red',
      finished: 'green',
      canceled: 'red',
    },
  }
  histories.value = historyResponse.data
    .filter(history => {
      return (history.type === 'Booking' && history.resource.id !== props.booking.id) || history.type === 'Allotment';
    });

  markers.value = histories.value
    .map(history => {
      const startAt = new Date(history.start_at);
      const endAt = new Date(history.end_at);
      const days = (endAt - startAt) / (1000 * 60 * 60 * 24);
      return Array.from({ length: days }, (_, i) => {
        return {
          date: new Date(startAt.getTime() + (i * 24 * 60 * 60 * 1000)),
          type: 'dot',
          tooltip: [
            {
              text: history.type === 'Allotment' ? history.resource.title : history.type,
              color: history.type === 'Booking' ? contentColor.booking[history.resource.status] : 'gray'
            }
          ],
          color: history.type === 'Booking' ? contentColor.booking[history.resource.status] : 'gray',
        };
      });
    }).flat();
}

onMounted(() => {
  getHistory(dayjs().month(), dayjs().year())
})
</script>

<style scoped>

</style>